import { Plus } from "@phosphor-icons/react";
import { useMemo, useState, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export type SelectableChipOption = {
  value: string;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
};

export type SelectableChipAction = {
  label: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
};

export type SelectableChipGroupProps = {
  options: SelectableChipOption[];
  value?: string | string[];
  defaultValue?: string | string[];
  multiple?: boolean;
  onValueChange?: (value: string | string[]) => void;
  addAction?: SelectableChipAction;
  className?: string;
  "aria-label"?: string;
};

const toArray = (value: string | string[] | undefined) => {
  if (Array.isArray(value)) return value;
  return value ? [value] : [];
};

export function SelectableChipGroup({ options, value, defaultValue, multiple, onValueChange, addAction, className, "aria-label": ariaLabel }: SelectableChipGroupProps) {
  const [internalValue, setInternalValue] = useState<string[]>(() => toArray(defaultValue));
  const selectedValues = useMemo(() => new Set(toArray(value ?? internalValue)), [internalValue, value]);

  const commitValue = (nextValues: string[]) => {
    const normalized = Array.from(new Set(nextValues));
    if (value === undefined) setInternalValue(normalized);
    onValueChange?.(multiple ? normalized : normalized[0] ?? "");
  };

  const toggleValue = (nextValue: string) => {
    if (multiple) {
      commitValue(selectedValues.has(nextValue) ? Array.from(selectedValues).filter((item) => item !== nextValue) : [...selectedValues, nextValue]);
      return;
    }

    commitValue([nextValue]);
  };

  return (
    <div role={multiple ? "group" : "radiogroup"} aria-label={ariaLabel} className={cn("bfrs-flex bfrs-flex-wrap bfrs-gap-2", className)}>
      {options.map((option) => {
        const selected = selectedValues.has(option.value);
        return (
          <button
            key={option.value}
            type="button"
            role={multiple ? "checkbox" : "radio"}
            aria-checked={selected}
            disabled={option.disabled}
            className={cn(
              "bfrs-inline-flex bfrs-min-h-8 bfrs-items-center bfrs-gap-1.5 bfrs-rounded-pill bfrs-border bfrs-px-3 bfrs-text-xs bfrs-font-semibold bfrs-outline-none bfrs-transition focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/25 disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-55",
              selected ? "bfrs-border-primary bfrs-bg-primary bfrs-text-primary-contrast" : "bfrs-border-border-control bfrs-bg-surface-card bfrs-text-text-secondary hover:bfrs-border-primary/60 hover:bfrs-bg-primary-soft"
            )}
            onClick={() => toggleValue(option.value)}
          >
            {option.icon}
            <span className="bfrs-truncate">{option.label}</span>
          </button>
        );
      })}
      {addAction && (
        <button
          type="button"
          disabled={addAction.disabled}
          className="bfrs-inline-flex bfrs-min-h-8 bfrs-items-center bfrs-gap-1.5 bfrs-rounded-pill bfrs-border bfrs-border-dashed bfrs-border-border-control bfrs-bg-surface-card bfrs-px-3 bfrs-text-xs bfrs-font-semibold bfrs-text-primary bfrs-outline-none bfrs-transition hover:bfrs-border-primary hover:bfrs-bg-primary-soft focus-visible:bfrs-ring-2 focus-visible:bfrs-ring-border-focus/25 disabled:bfrs-cursor-not-allowed disabled:bfrs-opacity-55"
          onClick={addAction.onClick}
        >
          <Plus size={13} weight="bold" />
          <span className="bfrs-truncate">{addAction.label}</span>
        </button>
      )}
    </div>
  );
}
